import { format } from 'date-fns';
import { convertTokens } from '@date-fns/upgrade/v2';

import { Transaction, Transactions } from './types';

export interface TransactionsAggregate {
  group: string;
  monthKey: string;
  paidAmount: number;
  receivedAmount: number;
  transactions: Transactions;
}

export interface SummarizedTransactionsAggregate {
  paidAmount: number;
  receivedAmount: number;
  transactionAggregates: TransactionsAggregate[];
}

export interface SummarizedTransactions {
  paidAmount: number;
  receivedAmount: number;
  processedTransactions: TransactionsAggregate[];
  pendingPaidAmount: number;
  pendingReceivedAmount: number;
  pendingTransactions: TransactionsAggregate[];
}

interface AggregatedTransactions {
  [key: string]: TransactionsAggregate;
}

export const getTransactionDateForTransaction = (transaction: Transaction) => {
  switch (transaction?.__typename) {
    case 'GivingAccountFunding':
    case 'GivingAccountWithdrawal':
      return transaction.processedAt || transaction.createdAt;

    default:
      return transaction.createdAt;
  }
};

const getMonthKeyForTransaction = (transaction: Transaction) => {
  const transactionDate = new Date(getTransactionDateForTransaction(transaction).substring(0, 10));
  return format(transactionDate, convertTokens('YYYY-MM'));
};

const getGroupForTransaction = (transaction: Transaction) => {
  const monthKey = getMonthKeyForTransaction(transaction);

  switch (transaction?.__typename) {
    case 'GivingAccountWithdrawal':
      return transaction.status === 'Processed' || transaction.processedAt ? monthKey : 'pending';

    default:
      return monthKey;
  }
};

export const summarizeTransactionAggregates = (aggregatedTransactions: AggregatedTransactions) =>
  Object.values(aggregatedTransactions || {}).reduce(
    (summary, transactionAggregate) => ({
      paidAmount: summary.paidAmount + transactionAggregate?.paidAmount,
      receivedAmount: summary.receivedAmount + transactionAggregate?.receivedAmount,
      transactionAggregates: transactionAggregate
        ? [...summary.transactionAggregates, transactionAggregate]
        : summary.transactionAggregates,
    }),
    {
      paidAmount: 0,
      receivedAmount: 0,
      transactionAggregates: [],
    } as SummarizedTransactionsAggregate
  );

export const summarizeTransactions = (transactions: Transactions): SummarizedTransactions => {
  const {
    pending: pendingTransactionsAggregate,
    ...processedTransactionsAggregatesByMonth
  }: AggregatedTransactions = transactions.reduce((acc, curr) => {
    const group = getGroupForTransaction(curr);
    const monthKey = getMonthKeyForTransaction(curr);

    if (!acc[group]) {
      acc[group] = {
        monthKey,
        group,
        paidAmount: 0,
        receivedAmount: 0,
        transactions: [],
      };
    }

    if (curr?.__typename === 'GivingAccountFunding') {
      acc[group].receivedAmount += curr.amount;
    }

    if (curr?.__typename === 'GivingAccountWithdrawal') {
      acc[group].paidAmount += curr.totalAccountCurrency || 0;
    }

    if (curr?.__typename === 'GivingAccountTransferOutbound') {
      acc[group].paidAmount += curr.amount || 0;
    }

    if (curr?.__typename === 'GivingAccountTransferInbound') {
      acc[group].paidAmount += curr.amount || 0;
    }

    acc[group].transactions.push(curr);

    return acc;
  }, {} as AggregatedTransactions);

  const {
    receivedAmount,
    paidAmount,
    transactionAggregates: transactionsByMonth,
  } = summarizeTransactionAggregates(processedTransactionsAggregatesByMonth);
  const {
    receivedAmount: pendingReceivedAmount,
    paidAmount: pendingPaidAmount,
    transactionAggregates: pendingTransactions,
  } = summarizeTransactionAggregates(
    pendingTransactionsAggregate && {
      pending: pendingTransactionsAggregate,
    }
  );

  return {
    receivedAmount,
    paidAmount,
    processedTransactions: transactionsByMonth,
    pendingReceivedAmount,
    pendingPaidAmount,
    pendingTransactions,
  };
};

export default summarizeTransactions;
